import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { getColorMode } from '../../theme/colorMode';
import { useColorMode } from '../../theme/ColorModeProvider';
import { useRouteTransition } from '../RouteTransition';

const logoSrc = `${process.env.PUBLIC_URL}/deviare-minimum-logo.png`;

let transparentLogoPromise;

const loadTransparentLogo = (src) => {
  if (!transparentLogoPromise) {
    transparentLogoPromise = new Promise((resolve) => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;
        const context = canvas.getContext('2d');
        context.drawImage(image, 0, 0);
        const frame = context.getImageData(0, 0, canvas.width, canvas.height);
        const { data } = frame;
        for (let index = 0; index < data.length; index += 4) {
          const red = data[index];
          const green = data[index + 1];
          const blue = data[index + 2];
          const brightness = (red + green + blue) / 3;
          const spread = Math.max(red, green, blue) - Math.min(red, green, blue);
          if (brightness > 242 && spread < 18) {
            data[index + 3] = 0;
          } else if (brightness > 214 && spread < 28) {
            const fade = (242 - brightness) / 28;
            data[index + 3] = Math.round(data[index + 3] * Math.max(0, Math.min(1, fade)));
          }
        }
        context.putImageData(frame, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      image.onerror = () => resolve(src);
      image.src = src;
    });
  }
  return transparentLogoPromise;
};

const useStyles = makeStyles((theme) => ({
  viewport: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: theme.zIndex.modal + 1,
    pointerEvents: 'none',
  },
  overlay: {
    backgroundColor: 'rgba(244, 247, 250, 0.72)',
    backdropFilter: 'blur(3px)',
  },
  overlayDark: {
    backgroundColor: 'rgba(6, 12, 24, 0.55)',
  },
  placeholder: {
    width: '100%',
    minHeight: 220,
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    width: 212,
    minHeight: 145,
    boxSizing: 'border-box',
    flex: '0 0 auto',
    padding: '22px 32px 16px',
    borderRadius: 16,
    background: 'linear-gradient(180deg, #ffffff 0%, #f4f7fb 100%)',
    border: '1px solid #e1e7ee',
    boxShadow: '0 12px 32px rgba(30, 48, 70, 0.12)',
  },
  panelDark: {
    background: 'radial-gradient(120px 80px at 50% 42%, rgba(61, 125, 221, 0.28), transparent 70%), linear-gradient(180deg, #12233d 0%, #0b1628 100%)',
    border: '1px solid rgba(148, 186, 255, 0.28)',
    boxShadow: '0 16px 40px rgba(6, 16, 36, 0.35)',
  },
  logoSlot: {
    width: 72,
    height: 83,
    flex: '0 0 83px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    filter: 'drop-shadow(0 4px 10px rgba(61, 125, 221, 0.22))',
    animation: '$pulse 1.4s ease-in-out infinite',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  logo: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
  },
  logoDark: {
    filter: 'drop-shadow(0 0 10px rgba(94, 196, 232, 0.45))',
  },
  '@keyframes pulse': {
    '0%': { opacity: 0.72 },
    '50%': { opacity: 1 },
    '100%': { opacity: 0.72 },
  },
  dots: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    minHeight: 22,
    color: '#3D7DDD',
    fontSize: '1.75rem',
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: '0.08em',
    '& span': {
      animation: '$dot 1.2s ease-in-out infinite',
    },
    '& span:nth-child(2)': {
      animationDelay: '0.2s',
    },
    '& span:nth-child(3)': {
      animationDelay: '0.4s',
    },
    '@media (prefers-reduced-motion: reduce)': {
      '& span': {
        animation: 'none',
        opacity: 1,
      },
    },
  },
  dotsDark: {
    color: '#8fd4f2',
  },
  '@keyframes dot': {
    '0%, 80%, 100%': { opacity: 0.25 },
    '40%': { opacity: 1 },
  },
}));

const AppLoader = ({ overlay = false, minHeight }) => {
  const classes = useStyles();
  const { mode } = useColorMode();
  const { isNavigationPending, isRouteLoading, registerRouteLoader } = useRouteTransition();
  const participatesInRouteTransition = React.useRef(isNavigationPending);
  const documentMode = typeof document !== 'undefined'
    ? document.documentElement.getAttribute('data-color-mode')
    : getColorMode();
  const isDark = mode === 'dark' || documentMode === 'dark';
  const [logo, setLogo] = React.useState('');

  React.useEffect(() => {
    let active = true;
    loadTransparentLogo(logoSrc).then((src) => {
      if (active) {
        setLogo(src);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  React.useEffect(() => {
    if (!participatesInRouteTransition.current) {
      return undefined;
    }
    return registerRouteLoader();
  }, [registerRouteLoader]);

  if (participatesInRouteTransition.current && isRouteLoading) {
    return overlay ? null : (
      <div
        className={classes.placeholder}
        style={minHeight != null ? { minHeight } : undefined}
        aria-hidden="true"
      />
    );
  }

  const loader = (
    <div
      className={`${classes.viewport} ${classes.overlay}${isDark ? ` ${classes.overlayDark}` : ''}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading"
    >
      <div
        className={`${classes.panel}${isDark ? ` ${classes.panelDark}` : ''}`}
        data-testid="app-loader-panel"
      >
        <div
          className={`${classes.logoSlot}${isDark ? ` ${classes.logoDark}` : ''}`}
          data-testid="app-loader-logo-slot"
          aria-hidden="true"
        >
          {logo ? <img className={classes.logo} src={logo} alt="" loading="eager" /> : null}
        </div>
        <div className={`${classes.dots}${isDark ? ` ${classes.dotsDark}` : ''}`} aria-hidden="true">
          <span>.</span>
          <span>.</span>
          <span>.</span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {!overlay ? (
        <div
          className={classes.placeholder}
          style={minHeight != null ? { minHeight } : undefined}
          aria-hidden="true"
        />
      ) : null}
      {typeof document !== 'undefined' ? ReactDOM.createPortal(loader, document.body) : loader}
    </>
  );
};

AppLoader.propTypes = {
  overlay: PropTypes.bool,
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default AppLoader;
